import React from "react";
import { Inbox, MessageSquare, Paperclip } from "lucide-react";
import Button from "../forms/Button";
import { SEVERITY_COLOR, STATUS_COLOR, TYPE_META, severityLabel, statusLabel, typeLabel } from "./constants";
import { avatarStyle, initialsOf, isArabic, pillStyle, relativeTime, tint } from "./format";

const Pill = ({ color, children }) => (
  <span className="er-pill" style={pillStyle(color)}><i />{children}</span>
);

const Row = ({ r, onOpen, showUnread }) => {
  const meta = TYPE_META[r.type];
  const Icon = meta.icon;
  const unread = showUnread && r.hasUpdate;
  const e = r.employee;
  return (
    <div
      className={`er-cols er-tr${unread ? " unread" : ""}${r.deletedByEmployee ? " deleted" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(r.id)}
      onKeyDown={(ev) => (ev.key === "Enter" || ev.key === " ") && (ev.preventDefault(), onOpen(r.id))}
    >
      <div className="er-row" style={{ gap: 12, minWidth: 0 }}>
        <div className="er-tile" style={{ width: 36, height: 36, borderRadius: 11, background: tint(meta.color, 14) }}>
          <Icon size={17} strokeWidth={1.9} color={meta.color} />
          {unread && <span className="er-udot" aria-label="New activity" />}
        </div>
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 3 }}>
          <div className="er-row" style={{ gap: 8, minWidth: 0 }}>
            <span dir="auto" className={`er-title${isArabic(r.title) ? " ar" : ""}`} style={{ fontWeight: unread ? 600 : 500 }}>{r.title}</span>
            {r.attachments.length > 0 && (
              <span className="er-count" title="Attachments"><Paperclip size={11} strokeWidth={2} />{r.attachments.length}</span>
            )}
            {r.replies.length > 0 && (
              <span className="er-count" title="Replies"><MessageSquare size={11} strokeWidth={2} />{r.replies.length}</span>
            )}
          </div>
          <div className="er-sub">
            <span style={{ flexShrink: 0 }}>{typeLabel(r.type)} ·</span>
            <span dir="auto">{r.description}</span>
          </div>
        </div>
      </div>

      {r.severity ? <Pill color={SEVERITY_COLOR[r.severity]}>{severityLabel(r.severity)}</Pill> : <span style={{ color: "var(--faint)" }}>—</span>}
      <Pill color={STATUS_COLOR[r.status]}>{statusLabel(r.status)}</Pill>

      <div className="er-row" style={{ gap: 10, minWidth: 0 }}>
        <div className="er-avatar" style={{ width: 30, height: 30, fontSize: 11, ...avatarStyle(e.name) }}>{initialsOf(e.name)}</div>
        <div style={{ minWidth: 0 }}>
          <div className="er-ellipsis" style={{ fontSize: 13.5, fontWeight: 500 }}>{e.name}</div>
          <div className="er-ellipsis" style={{ fontSize: 12, color: "var(--faint)" }}>
            {e.department?.name || "—"}{e.employeeCode ? ` · #${e.employeeCode}` : ""}
          </div>
        </div>
      </div>

      <span style={{ textAlign: "right", fontSize: 12.5, color: "var(--muted)", whiteSpace: "nowrap" }} title={new Date(r.createdAt).toLocaleString()}>
        {relativeTime(r.createdAt)}
      </span>
    </div>
  );
};

const RequestsTable = ({ requests, total, loading, loadingMore, hasMore, loadMore, sentinelRef, hasFilters, onClear, onOpen, showUnread }) => (
  <div className="er-card">
    <div className="er-table">
      <div className="er-cols er-th">
        <span>Request</span><span>Severity</span><span>Status</span><span>Employee</span><span style={{ textAlign: "right" }}>Submitted</span>
      </div>
      {requests.map((r) => <Row key={r.id} r={r} onOpen={onOpen} showUnread={showUnread} />)}
    </div>

    {!loading && requests.length === 0 && (
      <div className="er-empty">
        <div className="er-emptytile"><Inbox size={20} strokeWidth={1.9} /></div>
        <div style={{ fontFamily: '"General Sans", Satoshi, sans-serif', fontSize: 16, fontWeight: 600 }}>
          {hasFilters ? "No requests match" : "No requests yet"}
        </div>
        {hasFilters && <button type="button" className="er-btn er-link" onClick={onClear}>Clear filters</button>}
      </div>
    )}

    {/* Infinite-scroll sentinel: reaching it loads the next page. */}
    <div className="er-foot" ref={sentinelRef}>
      <span>{total} request{total === 1 ? "" : "s"}</span>
      {loadingMore ? (
        <span>Loading more…</span>
      ) : hasMore ? (
        <Button size="sm" variant="secondary" onClick={loadMore}>Load more</Button>
      ) : requests.length > 0 ? (
        <span className="er-mono">{requests.length} / {total}</span>
      ) : null}
    </div>
  </div>
);

export default RequestsTable;
